import React, { useMemo, useState } from 'react';
import {
  Box, Typography, IconButton, Button,
  Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import { ChevronLeft as ChevronLeftIcon, ChevronRight as ChevronRightIcon } from '@mui/icons-material';
import toast from 'react-hot-toast';
import { getMonday } from '../../utils/weekUtils';
import { getCapacityCellStyle } from '../../utils/capacityColors';
import { BACKEND_URL } from '../../utils/apiConfig';
import { authFetch } from '../../utils/authHelpers';
import { computeDayLoad, pctOfDay, isDraaidagStaat } from '../../utils/capacityCalc';
import {
  addDays, startOfDay, nextWorkDay, eindDatumVoor, formatISO, generateWorkingDays,
  werkdagenVoor, agendaVan,
} from './plannerDatums';

// ─── Speeltuin: hele deliverable bij één persoon inpassen (Joris, 1 sep) ─────
// Sleep een deliverable-chip op een persoon: dit dialoog stelt een opeenvolgende
// planning voor en laat je schuiven (nieuwe én bestaande taken) tot de dagen
// rond de 100% zitten — live dezelfde capaciteitsberekening als het overzicht.

const volgendeWerkdagNa = (d) => nextWorkDay(addDays(startOfDay(d), 1));
const prevWorkDayVoor = (iso) => {
  let d = addDays(new Date(`${String(iso).slice(0, 10)}T12:00:00`), -1);
  while (d.getDay() === 0 || d.getDay() === 6) d = addDays(d, -1);
  return startOfDay(d);
};

/** Synthetisch (draaidag uit de bron, afwezig uit de agenda): telt mee, maar is nooit te verschuiven of te patchen. */
export const isSynthetisch = (t) => !!(t?.alleenLezen || t?.bronDraaidag || /^(draaidag:|afwezig-)/.test(String(t?.id || '')));

/**
 * Alle bezetting voor de capaciteitsstrook: bewerkbare taken (met verschuivingen), het
 * concept, en de alleen-lezen bezetting (draaidagen, afwezig) — dezelfde bronnen als het rooster.
 */
export function speeltuinBezetting({ bestaand = [], concept = [], vast = [] }) {
  return [
    ...bestaand,
    ...concept.map(({ task, start, eind }) => ({ id: task.id, name: task.name, type: task.type, hours: task.hours, startDate: start, endDate: eind })),
    ...vast,
  ];
}

export default function SpeeltuinDialog({
  info, persons, existingTasks, vasteTaken = [], calendarHours, gedeeldeMails, rules, onClose, onKlaar,
}) {
  const person = persons.find((p) => p.id === info?.personId);
  const [busy, setBusy] = useState(false);

  // Conceptplanning: opeenvolgend vanaf de drop-dag.
  const [concept, setConcept] = useState(() => {
    const uit = [];
    let cursor = nextWorkDay(new Date(`${info.startDatum}T12:00:00`));
    (info.taken || []).forEach((t) => {
      const dagen = werkdagenVoor(t);
      const start = new Date(cursor);
      const eind = eindDatumVoor(start, dagen);
      uit.push({ task: t, start: formatISO(start), eind: formatISO(eind) });
      cursor = volgendeWerkdagNa(eind);
    });
    return uit;
  });
  // Verschuivingen van bestaande taken (id → {start, eind}).
  const [bestaandeShift, setBestaandeShift] = useState({});

  const bestaand = useMemo(() => (existingTasks[info.personId] || []).filter((t) => !isSynthetisch(t)).map((t) => (
    bestaandeShift[t.id] ? { ...t, startDate: bestaandeShift[t.id].start, endDate: bestaandeShift[t.id].eind } : t
  )), [existingTasks, info.personId, bestaandeShift]);

  // Venster: van de vroegste tot de laatste betrokken datum (min 2 weken).
  const dagen = useMemo(() => {
    const eerste = getMonday(new Date(`${info.startDatum}T12:00:00`));
    return generateWorkingDays(eerste, 4);
  }, [info.startDatum]);
  const dagISOs = useMemo(() => dagen.map((d) => d.iso), [dagen]);

  const alleTaken = useMemo(
    () => speeltuinBezetting({ bestaand, concept, vast: vasteTaken }),
    [bestaand, concept, vasteTaken]
  );

  const { uren, staat } = useMemo(
    () => computeDayLoad(alleTaken, dagISOs, agendaVan(calendarHours, person, gedeeldeMails), rules),
    [alleTaken, dagISOs, calendarHours, gedeeldeMails, person, rules]
  );

  const schuifConcept = (idx, richting) => {
    setConcept((prev) => prev.map((c, i) => {
      if (idx !== null && i !== idx) return c;
      const start = richting > 0 ? volgendeWerkdagNa(new Date(`${c.start}T12:00:00`)) : prevWorkDayVoor(c.start);
      const dagenT = werkdagenVoor(c.task);
      return { ...c, start: formatISO(start), eind: formatISO(eindDatumVoor(start, dagenT)) };
    }));
  };
  const schuifBestaand = (t, richting) => {
    const s0 = new Date(`${String(t.startDate).slice(0, 10)}T12:00:00`);
    const e0 = new Date(`${String(t.endDate || t.startDate).slice(0, 10)}T12:00:00`);
    const s1 = richting > 0 ? volgendeWerkdagNa(s0) : prevWorkDayVoor(formatISO(s0));
    // Echte werkdagen tellen: kalenderdagen als werkdagen interpreteren rekte
    // een vr–ma-taak na verschuiven op tot donderdag (Codex 1 sep, punt 14).
    let dagenT = 0;
    for (let d = new Date(s0); d <= e0; d = addDays(d, 1)) {
      const dw = d.getDay();
      if (dw !== 0 && dw !== 6) dagenT += 1;
    }
    dagenT = Math.max(1, dagenT);
    setBestaandeShift((prev) => ({ ...prev, [t.id]: { start: formatISO(s1), eind: formatISO(eindDatumVoor(s1, Math.min(dagenT, 10))) } }));
  };

  const toepassen = async () => {
    setBusy(true);
    try {
      const calls = [
        ...concept.map(({ task, start, eind }) => authFetch(`${BACKEND_URL}/Api/tasks/${task.id}`, {
          method: 'PATCH', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ Assignee: [info.personId], StartDateUitvoerder: start, EndDateUitvoerder: eind, NogInTePlannen: false }),
        })),
        ...Object.entries(bestaandeShift).map(([id, w]) => authFetch(`${BACKEND_URL}/Api/tasks/${id}`, {
          method: 'PATCH', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ StartDateUitvoerder: w.start, EndDateUitvoerder: w.eind }),
        })),
      ];
      const uitkomsten = await Promise.all(calls.map((c) => c.then((r) => r.ok).catch(() => false)));
      const fouten = uitkomsten.filter((ok) => !ok).length;
      if (fouten) toast.error(`${fouten} taken konden niet worden opgeslagen`);
      else toast.success(`${concept.length} taken ingepland bij ${person?.firstName || person?.name}`);
      onKlaar();
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onClose={() => !busy && onClose()} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontWeight: 800 }}>
        🧩 {info.deliverableName} → {person?.firstName || person?.name}
      </DialogTitle>
      <DialogContent dividers>
        {/* Capaciteitsstrook: live, inclusief het concept */}
        <Box sx={{ display: 'flex', gap: '2px', mb: 2, flexWrap: 'wrap' }}>
          {dagen.map((d) => {
            const pct = pctOfDay(uren[d.iso] || 0, rules.hoursPerDay);
            const stijl = getCapacityCellStyle(pct);
            const vrij = staat[d.iso] === 'vrij';
            const draaidag = isDraaidagStaat(staat[d.iso]);
            return (
              <Box key={d.iso} data-testid={`speeltuin-dag-${d.iso}`} sx={{ width: 40, textAlign: 'center', borderRadius: 0.5, py: 0.25,
                bgcolor: vrij ? '#e0f2fe' : stijl.bg }}>
                <Typography sx={{ fontSize: 8.5, color: '#64748b' }}>{d.dayName} {d.dayNum}</Typography>
                <Typography sx={{ fontSize: 9.5, fontWeight: 700, color: vrij ? '#0369a1' : stijl.text }}>
                  {vrij ? 'Afwezig' : draaidag ? 'Draaidag' : `${pct}%`}
                </Typography>
              </Box>
            );
          })}
        </Box>

        <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.5 }}>Nieuwe taken ({info.deliverableName})</Typography>
        <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
          <Button size="small" onClick={() => schuifConcept(null, -1)} sx={{ textTransform: 'none', py: 0 }}>← alles 1 dag</Button>
          <Button size="small" onClick={() => schuifConcept(null, 1)} sx={{ textTransform: 'none', py: 0 }}>alles 1 dag →</Button>
        </Box>
        {concept.map((c, i) => (
          <Box key={c.task.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.25 }}>
            <Typography noWrap sx={{ fontSize: 12.5, flex: 1, minWidth: 0 }}>{c.task.name}</Typography>
            <Typography sx={{ fontSize: 12, color: '#64748b', whiteSpace: 'nowrap' }}>{c.start}{c.eind !== c.start ? ` – ${c.eind}` : ''}</Typography>
            <IconButton size="small" onClick={() => schuifConcept(i, -1)} sx={{ p: 0.25 }}><ChevronLeftIcon sx={{ fontSize: 15 }} /></IconButton>
            <IconButton size="small" onClick={() => schuifConcept(i, 1)} sx={{ p: 0.25 }}><ChevronRightIcon sx={{ fontSize: 15 }} /></IconButton>
            {c.task.deadline && String(c.eind) > String(c.task.deadline).slice(0, 10) && (
              <Typography sx={{ fontSize: 11 }} title={`Deadline ${String(c.task.deadline).slice(0, 10)} wordt overschreden`}>⚠️</Typography>
            )}
          </Box>
        ))}

        {bestaand.length > 0 && (
          <>
            <Typography variant="subtitle2" sx={{ fontWeight: 800, mt: 2, mb: 0.5 }}>
              Al ingepland bij {person?.firstName || person?.name} — schuif om ruimte te maken
            </Typography>
            {bestaand
              .filter((t) => (t.startDate && dagISOs.includes(String(t.startDate).slice(0, 10))) || bestaandeShift[t.id])
              .slice(0, 15)
              .map((t) => (
                <Box key={t.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.25, opacity: 0.85 }}>
                  <Typography noWrap sx={{ fontSize: 12, flex: 1, minWidth: 0, color: '#475569' }}>{t.name}</Typography>
                  <Typography sx={{ fontSize: 11.5, color: '#94a3b8', whiteSpace: 'nowrap' }}>
                    {String(t.startDate).slice(0, 10)}{t.endDate && t.endDate !== t.startDate ? ` – ${String(t.endDate).slice(0, 10)}` : ''}
                    {bestaandeShift[t.id] ? ' (aangepast)' : ''}
                  </Typography>
                  <IconButton size="small" onClick={() => schuifBestaand(t, -1)} sx={{ p: 0.25 }}><ChevronLeftIcon sx={{ fontSize: 14 }} /></IconButton>
                  <IconButton size="small" onClick={() => schuifBestaand(t, 1)} sx={{ p: 0.25 }}><ChevronRightIcon sx={{ fontSize: 14 }} /></IconButton>
                </Box>
              ))}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy} sx={{ textTransform: 'none' }}>Annuleren</Button>
        <Button variant="contained" onClick={toepassen} disabled={busy || !concept.length}
          sx={{ textTransform: 'none', fontWeight: 700 }}>
          {busy ? 'Bezig…' : `Plan ${concept.length} taken in`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
